import { useState, useRef, useEffect, FormEvent } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const FORM_URL = 'https://readdy.ai/api/form/d72iid1sakbatd8b3l2g';

export default function Contact() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.querySelectorAll('.contact-animate').forEach((item, i) => {
              setTimeout(() => {
                item.classList.add('animate-fade-up');
                item.classList.remove('opacity-0-init');
              }, i * 100);
            });
            observer.disconnect();
          }
        });
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = formRef.current;
    if (!form) return;

    const description = (form.elements.namedItem('description') as HTMLTextAreaElement)?.value || '';
    if (description.length > 500) {
      return;
    }

    setStatus('sending');
    const data = new URLSearchParams();
    const elements = form.elements;
    for (let i = 0; i < elements.length; i++) {
      const el = elements[i] as HTMLInputElement | HTMLTextAreaElement;
      if (el.name && el.value) {
        data.append(el.name, el.value);
      }
    }

    try {
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (response.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <main>
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-0 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pb-12 md:pb-16">
          <span className="block text-xs font-medium tracking-[0.3em] text-stone-400 uppercase mb-4 animate-fade-up">
            Contact
          </span>
          <h1 className="font-serif-display text-4xl md:text-6xl font-bold text-stone-900 leading-tight max-w-2xl animate-fade-up delay-100">
            Discutons de votre projet
          </h1>
        </div>
      </section>

      {/* Main content */}
      <section ref={sectionRef} className="py-16 md:py-24 px-6 md:px-10 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">

          {/* Form – 3 cols */}
          <div className="lg:col-span-3">
            <h2 className="contact-animate opacity-0-init text-sm font-semibold text-stone-400 uppercase tracking-widest mb-8">
              Formulaire de contact
            </h2>

            {status === 'success' ? (
              <div className="contact-animate opacity-0-init bg-[#E4F0E8] rounded-2xl p-10 text-center">
                <div className="w-12 h-12 flex items-center justify-center bg-stone-900 rounded-full mx-auto mb-4">
                  <i className="ri-check-line text-white text-xl" />
                </div>
                <h3 className="text-xl font-bold text-stone-900 mb-2">Message envoyé !</h3>
                <p className="text-stone-600 text-sm">Nous vous répondrons dans les meilleurs délais.</p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-6 px-6 py-2.5 border border-stone-300 text-stone-700 text-sm rounded-full hover:border-stone-900 hover:text-stone-900 transition-colors duration-150 cursor-pointer"
                >
                  Envoyer un autre message
                </button>
              </div>
            ) : (
              <form
                ref={formRef}
                id="contact-kurta-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="flex flex-col gap-6"
              >
                {/* Name */}
                <div className="contact-animate opacity-0-init flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-stone-500 tracking-widest uppercase" htmlFor="name">
                    Nom
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    placeholder="Votre nom"
                    className="w-full h-12 px-4 text-sm border border-stone-200 rounded-lg bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:border-stone-400 transition-colors duration-150"
                  />
                </div>

                {/* Email */}
                <div className="contact-animate opacity-0-init flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-stone-500 tracking-widest uppercase" htmlFor="email">
                    Email
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="votre@email.com"
                    className="w-full h-12 px-4 text-sm border border-stone-200 rounded-lg bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:border-stone-400 transition-colors duration-150"
                  />
                </div>

                {/* Phone */}
                <div className="contact-animate opacity-0-init flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-stone-500 tracking-widest uppercase" htmlFor="telephone">
                    Téléphone
                  </label>
                  <input
                    id="telephone"
                    name="telephone"
                    type="tel"
                    placeholder="+33 6 00 00 00 00"
                    className="w-full h-12 px-4 text-sm border border-stone-200 rounded-lg bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:border-stone-400 transition-colors duration-150"
                  />
                </div>

                {/* Description */}
                <div className="contact-animate opacity-0-init flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-stone-500 tracking-widest uppercase" htmlFor="description">
                    Description du projet
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    rows={5}
                    required
                    maxLength={500}
                    placeholder="Décrivez votre projet en quelques mots…"
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full px-4 py-3 text-sm border border-stone-200 rounded-lg bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:border-stone-400 transition-colors duration-150 resize-none"
                  />
                  <span className={`text-xs text-right ${charCount > 480 ? 'text-red-500' : 'text-stone-400'}`}>
                    {charCount}/500
                  </span>
                </div>

                {status === 'error' && (
                  <p className="text-sm text-red-500">Une erreur est survenue. Veuillez réessayer.</p>
                )}

                <button
                  type="submit"
                  disabled={status === 'sending' || charCount > 500}
                  className="contact-animate opacity-0-init w-full h-12 flex items-center justify-center gap-2 bg-stone-900 text-white text-sm font-medium rounded-full hover:bg-stone-700 disabled:opacity-60 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  {status === 'sending' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center" />
                      Envoi en cours…
                    </>
                  ) : (
                    <>
                      Envoyer
                      <i className="ri-send-plane-line text-base w-4 h-4 flex items-center justify-center" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Info – 2 cols */}
          <div className="lg:col-span-2 flex flex-col gap-8">
            <div className="contact-animate opacity-0-init">
              <h3 className="text-xs font-semibold text-stone-400 uppercase tracking-widest mb-5">Entreprise</h3>
              <p className="font-bold text-stone-900 text-base">KURTA</p>
              <p className="text-stone-600 text-sm mt-1">Dawid Kurta</p>
              <p className="text-stone-500 text-xs mt-1">SIRET : 102 042 504 00013</p>
            </div>

            <div className="contact-animate opacity-0-init border-t border-stone-100 pt-6">
              <h3 className="text-xs font-semibold text-stone-400 uppercase tracking-widest mb-4">Adresse</h3>
              <address className="not-italic text-sm text-stone-600 leading-relaxed">
                2 Rue Pelleport<br />
                33800 Bordeaux<br />
                France
              </address>
            </div>

            <div className="contact-animate opacity-0-init border-t border-stone-100 pt-6">
              <h3 className="text-xs font-semibold text-stone-400 uppercase tracking-widest mb-4">Téléphone</h3>
              <a
                href="tel:+33751313477"
                className="text-sm text-stone-700 hover:text-stone-900 transition-colors duration-150"
              >
                +33 7 51 31 34 77
              </a>
            </div>

            <div className="contact-animate opacity-0-init border-t border-stone-100 pt-6">
              <h3 className="text-xs font-semibold text-stone-400 uppercase tracking-widest mb-4">Email</h3>
              <a
                href="mailto:daw1dkurta@inbox.eu"
                className="text-sm text-stone-700 hover:text-stone-900 transition-colors duration-150 break-all"
              >
                daw1dkurta@inbox.eu
              </a>
            </div>

            {/* Map embed */}
            <div className="contact-animate opacity-0-init rounded-2xl overflow-hidden border border-stone-100">
              <iframe
                title="KURTA Bordeaux"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2835.2!2d-0.5792!3d44.8378!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMiBSdWUgUGVsbGVwb3J0LCAzMzgwMCBCb3JkZWF1eA!5e0!3m2!1sfr!2sfr!4v1"
                width="100%"
                height="200"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full h-[200px]"
              />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}